import axios, { AxiosRequestConfig, isAxiosError } from "axios"

import URLS from "~/constants/urls"

type RetryFetchOptions = RequestInit & {
  retries?: number
  retryDelay?: number
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

export const retryFetch = async (
  url: string,
  options: RetryFetchOptions = {}
): Promise<Response> => {
  const { retries = 3, retryDelay = 1000, ...fetchOptions } = options
  const method = fetchOptions.method?.toUpperCase() ?? "GET"

  const shouldRetry = method === "GET" || method === "POST"

  let lastError: Error | null = null

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url, fetchOptions)

      if (shouldRetry && response.status >= 500 && attempt < retries) {
        await sleep(retryDelay * Math.pow(2, attempt))
        continue
      }

      return response
    } catch (error) {
      lastError = error as Error

      if (shouldRetry && attempt < retries) {
        await sleep(retryDelay * Math.pow(2, attempt))
        continue
      }

      throw error
    }
  }

  throw lastError || new Error("Fetch failed after retries")
}

export const axiosInstance = axios.create({
  baseURL: URLS.V1,
  headers: {
    "Content-Type": "application/json"
  }
})

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (isAxiosError(error) && error.response?.status === 401) {
      await fetch("/logout", { method: "POST" })
      window.location.href = "/login"
      return Promise.reject(error)
    }

    return Promise.reject(error)
  }
)

export const fetchDataOnServer = async (
  options: AxiosRequestConfig,
  request: Request
) => {
  try {
    const response = await axios({
      ...options,
      headers: {
        ...options.headers
      }
    })
    return response.data
  } catch (error) {
    if (isAxiosError(error)) {
      if (error.response?.status === 401) {
        try {
          const { logout } = await import("~/session")
          await logout(request)
        } catch {
          // do nothing
        }
        return {
          error: error.response.data?.error?.message,
          status: error.response?.status
        }
      } else if (error.response?.status === 400) {
        return {
          error: error.response.data?.error?.message,
          status: error.response?.status
        }
      } else {
        return {
          error: error.response?.data?.error?.message,
          status: error.response?.status
        }
      }
    }
    throw error
  }
}
